import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, normalizeStyle as _normalizeStyle, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-76627fc7"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["onMouseover"]
const _hoisted_2 = ["onClick"]
const _hoisted_3 = { class: "text-red" }
const _hoisted_4 = { class: "include-cell" }

import { computed, PropType, ref, watch } from "vue";
import { DxTextBox } from "devextreme-vue/text-box";
import GridBase from "@/components/ui/grid/grid-base.vue";
import { DxColumn } from "devextreme-vue/data-grid";
import SwitchToggle from "@/components/ui/toggles/switch-toggle.vue";
import { useScreenHelper } from "@/utils/system/ScreenHelper";
import { getCurrencyFormat } from "@/utils/CurrencyFormatter";
import { useUserHelper } from "@/utils/users/useUserHelper";
import {
  ConsolidatedPaymentFormItem,
  ConsolidatedToPayItem,
} from "@/types/financials/ConsolidatedPayment";
import StatementNotesModal from "@/components/accounts/statement-notes-modal.vue";
import { FinancialDefaults } from "@/utils/financials/FinancialDefaults";
import { useFinancialHelper } from "@/utils/financials/useFinancialHelper";
import { useContactHelper } from "@/utils/contacts/useContactHelper";
import { sleep } from "@/utils/system/TimeoutHelper";
import SpinnerComponent from "@/layouts/spinner-component.vue";
import ContactsPeekPopup from "@/components/contacts/contacts-peek-popup.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'consolidated-payment-grid',
  props: {
  /**
   * Form data object containing ConsolidatedPaymentFormItem details.
   * @type {ConsolidatedPaymentFormItem}
   * @required true
   */
  formItem: {
    type: Object as PropType<ConsolidatedPaymentFormItem>,
    required: true,
  },
  /**
   * Array containing grid data of ConsolidatedToPayItem object.
   * @type {ConsolidatedToPayItem}
   * @required true
   */
  gridCredits: {
    type: Object as PropType<ConsolidatedToPayItem[]>,
    required: true,
  },
  /**
   * Indicates whether data is being loaded and the grid should show a loading state.
   * @type {Boolean}
   * @required true
   */
  loading: {
    type: Boolean,
    required: true,
  },
  /**
   * Determines whether the control is read only.
   * @type {boolean}
   * @default true
   */
  readOnly: {
    type: Boolean,
    default: true,
  },
},
  emits: [
  /**
   * Emits the updated credit grid data to the parent component
   * when the include status of any item changes via toggle, select all, or clear actions.
   * @event update:gridCredits
   */
  "update:gridCredits",
],
  setup(__props, { expose: __expose, emit }) {

const props = __props;





const { isMobile } = useScreenHelper();
const userHelper = useUserHelper();
const contactHelper = useContactHelper();
const financialHelper = useFinancialHelper();
const gridKey = ref(0);
const exportGrid = ref(false);
const selectedRowItem = ref<ConsolidatedToPayItem>(
  FinancialDefaults.defaultConsolidatedToPayItem(),
);
const displayStatementNotesModal = ref(false);
const loadingReport = ref(false);
const hoverContact = ref<ConsolidatedToPayItem>(
  FinancialDefaults.defaultConsolidatedToPayItem(),
);
const contactIconId = ref(0);
const peekModalKey = ref(0);
const showPeek = ref(false);

const localList = computed({
  get: () => props.gridCredits.map((item) => ({ ...item })),
  set: (value) => {
    emit("update:gridCredits", value);
  },
});

const setAllIncludeStatus = (status: boolean) => {
  const updatedList = localList.value.map((item) => ({
    ...item,
    include: status,
  }));
  localList.value = updatedList;
};

const tickAll = () => {
  setAllIncludeStatus(true);
};

const untickAll = () => {
  setAllIncludeStatus(false);
};

const paymentSummaryReport = async () => {
  loadingReport.value = true;
  const pdf = await financialHelper.paymentSummaryReport(
    props.formItem.bankAccountId,
    contactIds.value,
  );

  if (!pdf) {
    return;
  }

  // Create url
  const url = window.URL.createObjectURL(pdf);

  // Link allows us to download the pdf
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `consolidated-payment-summary-report-${Date.now()}.pdf`,
  );
  document.body.appendChild(link);
  link.click();

  // Clears memory
  link.remove();
  window.URL.revokeObjectURL(url);
  loadingReport.value = false;
};

const setFloatAmount = () => {
  displayStatementNotesModal.value = true;
};

const pauseUnpausePayments = async () => {
  const contactItem = await contactHelper.fetchContactById(
    selectedRowItem.value.contactId,
  );
  if (contactItem) {
    const booPause = !selectedRowItem.value.pausePaymentRun;
    // Invert pause flag
    contactItem.pausePaymentRun = booPause ? 1 : 0;
    // Update Contact
    await contactHelper.updateContact(contactItem);

    if (booPause) {
      // Pause payments - update row and stop Include being ticked
      selectedRowItem.value.pausePaymentRun = booPause;
      selectedRowItem.value.warnings = `Payments Paused\n${selectedRowItem.value.warnings}`;
      selectedRowItem.value.disableInclude = true;

      if (selectedRowItem.value.include) {
        selectedRowItem.value.include = false;
      }
    } else {
      // Reset Pause Payments flag
      selectedRowItem.value.pausePaymentRun = false;

      // Remove warning
      selectedRowItem.value.warnings = selectedRowItem.value.warnings.replace(
        /Payments Paused/g,
        "",
      );

      // Only enable Include cell if we haven't also disabled it for uncleared funds
      if (selectedRowItem.value.disableUnclearedFunds === 0) {
        selectedRowItem.value.disableInclude = false;
      }
    }
  }
};

const exportToExcel = () => {
  exportGrid.value = true;
};

const onSetFloatAmount = async (value: number) => {
  const contactItem = await contactHelper.fetchContactById(
    selectedRowItem.value.contactId,
  );
  if (contactItem) {
    contactItem.consolidatedFloat = value;
    await contactHelper.updateContact(contactItem);
    await sleep(300);
    selectedRowItem.value.consolidatedFloat = value;
  }
};

watch(
  [() => props.gridCredits],
  async () => {
    gridKey.value++;
    if (props.gridCredits.length > 0) {
      selectedRowItem.value = props.gridCredits[0];
    }
  },
  { deep: true },
);

const includedItems = computed(() => {
  return localList.value.filter((item) => item.include);
});

const contactIds = computed<number[]>(() => {
  return includedItems.value.map((item) => item.contactId);
});

const contactFieldWidth = computed(() => (isMobile.value ? "180" : undefined));

const onRowSelected = (e: ConsolidatedToPayItem) => {
  if (!e) return;

  selectedRowItem.value = e;
};

const navigateToContact = (rowItem: ConsolidatedToPayItem) => {
  hoverContact.value = rowItem;
  showPeek.value = true;
};

const displayContactIcon = (rowItem: ConsolidatedToPayItem) => {
  contactIconId.value = rowItem.contactId;
};

const hideContactIcon = () => {
  contactIconId.value = 0;
};

const gridHeight = computed(() => {
  return isMobile.value ? "" : `height: 330px;`;
});

// Expose these methods to parent component
__expose({
  tickAll,
  untickAll,
  paymentSummaryReport,
  setFloatAmount,
  pauseUnpausePayments,
  exportToExcel,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (loadingReport.value)
      ? (_openBlock(), _createBlock(SpinnerComponent, { key: 0 }))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      style: _normalizeStyle(gridHeight.value)
    }, [
      (_openBlock(), _createBlock(GridBase, {
        "data-source": __props.gridCredits,
        key: gridKey.value,
        "id-column-name": "id",
        "run-export": exportGrid.value,
        "onUpdate:runExport": _cache[0] || (_cache[0] = ($event: any) => ((exportGrid).value = $event)),
        "column-auto-width": true,
        loading: __props.loading,
        wordWrapEnabled: true,
        showSearchPanel: true,
        "custom-column-slots": [
        'contactCell',
        'referenceChequeNoCell',
        'warningCell',
        'includeCell',
      ],
        preSelectedId: selectedRowItem.value?.id,
        onRowSelected: onRowSelected
      }, {
        columns: _withCtx(() => [
          _createVNode(_unref(DxColumn), {
            "data-field": "contact",
            caption: "Contact",
            width: contactFieldWidth.value,
            "cell-template": "contactCell",
            "hiding-priority": 12
          }, null, 8, ["width"]),
          _createVNode(_unref(DxColumn), {
            "data-field": "preferredPaymentDay",
            caption: "Preferred Payment Day",
            width: 120,
            "hiding-priority": 10
          }),
          _createVNode(_unref(DxColumn), {
            "data-type": "date",
            format: "dd/MM/yyyy",
            "data-field": "lastPaymentDate",
            caption: "Last Payment Date",
            width: 105,
            "hiding-priority": 9
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "rentArrears",
            caption: "Rent Arrears",
            "hiding-priority": 8,
            width: _unref(isMobile) ? undefined : 120,
            format: 
            _unref(getCurrencyFormat)(_unref(userHelper).currentUserDetails.value.countryCodeID)
          
          }, null, 8, ["width", "format"]),
          _createVNode(_unref(DxColumn), {
            "data-field": "warnings",
            caption: "Warnings",
            width: 120,
            "hiding-priority": 7,
            "cell-template": "warningCell"
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "referenceChequeNo",
            caption: "Reference / Cheque No.",
            width: 120,
            "hiding-priority": 6,
            "cell-template": "referenceChequeNoCell"
          }),
          _createVNode(_unref(DxColumn), {
            "data-field": "balance",
            caption: "Balance",
            "hiding-priority": 5,
            width: 110,
            format: 
            _unref(getCurrencyFormat)(_unref(userHelper).currentUserDetails.value.countryCodeID)
          
          }, null, 8, ["format"]),
          _createVNode(_unref(DxColumn), {
            "data-field": "consolidatedFloat",
            caption: "Float To Retain",
            "hiding-priority": 4,
            width: 100,
            format: 
            _unref(getCurrencyFormat)(_unref(userHelper).currentUserDetails.value.countryCodeID)
          
          }, null, 8, ["format"]),
          _createVNode(_unref(DxColumn), {
            "data-field": "taxToDeduct",
            caption: "Tax To Deduct",
            "hiding-priority": 3,
            width: 95,
            format: 
            _unref(getCurrencyFormat)(_unref(userHelper).currentUserDetails.value.countryCodeID)
          
          }, null, 8, ["format"]),
          _createVNode(_unref(DxColumn), {
            "data-field": "amountToPay",
            caption: "Amount To Pay",
            "hiding-priority": 2,
            width: 100,
            format: 
            _unref(getCurrencyFormat)(_unref(userHelper).currentUserDetails.value.countryCodeID)
          
          }, null, 8, ["format"]),
          _createVNode(_unref(DxColumn), {
            "data-field": "include",
            caption: "Include",
            width: 78,
            "hiding-priority": 11,
            "cell-template": "includeCell"
          })
        ]),
        contactCellData: _withCtx(({ data }) => [
          _createElementVNode("div", {
            class: "property-contact-cell",
            onMouseover: ($event: any) => (displayContactIcon(data)),
            onMouseleave: hideContactIcon
          }, [
            (contactIconId.value == data.contactId)
              ? (_openBlock(), _createElementBlock("i", {
                  key: 0,
                  onClick: ($event: any) => (navigateToContact(data)),
                  class: "fas fa-user"
                }, null, 8, _hoisted_2))
              : _createCommentVNode("", true),
            _createTextVNode(" " + _toDisplayString(data.contact), 1)
          ], 40, _hoisted_1)
        ]),
        referenceChequeNoCellData: _withCtx(({ data }) => [
          _createVNode(_unref(DxTextBox), {
            modelValue: data.referenceChequeNo,
            "onUpdate:modelValue": ($event: any) => ((data.referenceChequeNo) = $event)
          }, null, 8, ["modelValue", "onUpdate:modelValue"])
        ]),
        warningCellData: _withCtx(({ data }) => [
          _createElementVNode("div", _hoisted_3, _toDisplayString(data.warnings), 1)
        ]),
        includeCellData: _withCtx(({ data }) => [
          _createElementVNode("div", _hoisted_4, [
            _createVNode(SwitchToggle, {
              modelValue: data.include,
              "onUpdate:modelValue": ($event: any) => ((data.include) = $event),
              "read-only": data.disableInclude || !__props.formItem.creditsEnable
            }, null, 8, ["modelValue", "onUpdate:modelValue", "read-only"])
          ])
        ]),
        _: 1
      }, 8, ["data-source", "run-export", "loading", "preSelectedId"]))
    ], 4),
    _createVNode(StatementNotesModal, {
      modelValue: displayStatementNotesModal.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((displayStatementNotesModal).value = $event)),
      title: 'Consolidated Payment Run',
      label: 'Set Float Amount',
      "is-number": true,
      "field-value": selectedRowItem.value.consolidatedFloat.toString(),
      onSaveClick: onSetFloatAmount
    }, null, 8, ["modelValue", "field-value"]),
    (_openBlock(), _createBlock(ContactsPeekPopup, {
      key: peekModalKey.value,
      modelValue: showPeek.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showPeek).value = $event)),
      contactId: hoverContact.value.contactId,
      title: hoverContact.value.contact
    }, null, 8, ["modelValue", "contactId", "title"]))
  ], 64))
}
}

})